import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import { destroySession } from "../../helpers/Session";
import dtrimage from '../../assets/dtr.png';
import toast from "../sweetalert/Sweetalert";
import { GenerateHRXLSX, GenerateInternalXLSX } from "../../helpers/GenerateXLSX";

const Employee = ({ isDepartmentHead, onEmployeeTimesheetsChange }) => {
    const navigate = useNavigate();

    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken');
    const uname = sessionStorage.getItem('uname') || ''; 
    const departmentSlug = sessionStorage.getItem('departmentslug');
    const timesheetChild = sessionStorage.getItem('timesheetChildSlug');
    const breadcrumbChildSlug = sessionStorage.getItem('timesheetChildSlug');
    const breadcrumbGrandchildSlug = sessionStorage.getItem('timesheetGrandchildSlug');
    const timesheetPayperiod = sessionStorage.getItem('timesheetPayperiod');
    const timesheetPayperiodId = sessionStorage.getItem('timesheetPayperiodId');
    
    const [accessibleDepartments, setAccessibleDepartments] = useState([]);
    const [showDownloadButtons, setShowDownloadButtons] = useState(0);
    const [showDownloadSpinner, setShowDownloadSpinner] = useState('');

    const { data: role } = useQuery({
        queryKey: ['userrole']
    });

    const { data: manageddepartments = [] } = useQuery({
        queryKey: ['managedDepartments', uname]
    });

    const { data: managedadjustments = [] } = useQuery({
        queryKey: ['managedAdjustments', uname]
    });
    
    useEffect(() => {
        const combineDepartments = [...manageddepartments, ...managedadjustments];
        const finalDepartments = combineDepartments.filter((value, index, self) => (
            index === self.findIndex((d) => (
                d.department_code === value.department_code && d.department === value.department
            ))
        ));

        if (JSON.stringify(finalDepartments) !== JSON.stringify(accessibleDepartments)) {
            setAccessibleDepartments(finalDepartments);
        }
    }, [manageddepartments, managedadjustments, accessibleDepartments]);

const { data: employees, isLoading: isLoadingEmployees, error: errorEmployees } = useQuery({
    queryKey: ['employees', { departmentSlug, timesheetChild, timesheetPayperiodId, isDepartmentHead, role, manageddepartments, managedadjustments }],
    queryFn: async () => {
        try {
            let departmentToQuery;
            // check if user is head of the department they're viewing
            // use the isDepartmentHead prop passed from parent and check specific department
            const isHeadOfViewingDepartment = isDepartmentHead;

            if (role === 'admin' || role === 'management') {
                departmentToQuery = timesheetChild;
            } else if (isDepartmentHead) {
                departmentToQuery = departmentSlug;
            } else {
                departmentToQuery = departmentSlug;
            }

            // for admin/management: only pass true if they're head of the viewing department
            // for others: pass their actual department head status
            const deptHeadParam = (role === 'admin' || role === 'management')
                ? (isHeadOfViewingDepartment ? 'true' : 'false')
                : (isDepartmentHead ? 'true' : 'false');

            // MSW-26192: Who can view unsubmitted payperiod cutoffs:
            // 1. Department Head (deptHeadParam === 'true')
            // 2. Approvers with manageddepartments/managedadjustments (hasApproverAccess)
            // Everyone else must wait until dept head submits the cutoff
            // Check approver access against the department being QUERIED (departmentToQuery)
            // Also check directly against manageddepartments/managedadjustments since accessibleDepartments might not be ready
            const hasApproverAccessViaState = accessibleDepartments?.some(dept => dept.department_code === departmentToQuery);
            const hasApproverAccessDirect = [...(manageddepartments || []), ...(managedadjustments || [])]
                .some(dept => dept.department_code === departmentToQuery);
            const hasApproverAccess = hasApproverAccessViaState || hasApproverAccessDirect;

            const finalDeptHeadParam = (
                deptHeadParam === 'true' ||  // Department Head
                hasApproverAccess            // Approvers (manageddepartments/managedadjustments)
            ) ? 'true' : 'false';

            const res = await axios.get(`dtr/users?department=${departmentToQuery}&payperiod=${timesheetPayperiodId}&userdepartment=${departmentSlug}&isDeptHead=${finalDeptHeadParam}`, {
                headers: {
                    'Authorization': `Bearer ${accesstoken}`
                }
            });

            if (res.data.status === 'success') {
                if (res.data.message === 'not_submitted') {

                    if (!isDepartmentHead) {
                        toast.fire({
                            icon: 'info',
                            title: 'Department timesheets are not yet submitted. Please contact your department head.'
                        });
                    } else {
                        toast.fire({
                            icon: 'info',
                            title: 'Your department timesheets are not yet submitted.'
                        });
                    }
                    return [];
                } else if (res.data.message === 'other_dept_not_submitted') {
                    toast.fire({
                        icon: 'info',
                        title: 'This department timesheets are not yet submitted. Please contact the department head.'
                    });
                    return [];
                } else if (res.data.message === 'no_data') {
                    return [];
                }
            }

            return res.data.response;
        } catch (error) {
            throw error;
        }
    },
    enabled: !!role && !!departmentSlug,
    retry: 1
});

    useEffect(() => {
        if (errorEmployees) {
            if (errorEmployees.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    }, [errorEmployees, navigate]);

    useEffect(() => {
        if (onEmployeeTimesheetsChange) {
            onEmployeeTimesheetsChange(employees?.length > 0);
        }
    }, [employees, onEmployeeTimesheetsChange]);

    const handleSetTimesheetEmployeeData = (uname, name) => {
        sessionStorage.setItem('timesheetEmployee', name);
        sessionStorage.setItem('timesheetEmployeeUname', uname);
    }

    const handleDownloadDTR = async (e, uname, type) => {
        e.preventDefault();
        setShowDownloadSpinner(type);

        try {
            const { data } = await axios.get(`dtr/timesheets/download?uname=${uname}&payperiod=${timesheetPayperiodId}&type=${type}`, {
                headers: {
                    'Authorization': `Bearer ${accesstoken}`
                }
            });
    
            if (data.status === 'success') {
                setShowDownloadSpinner('');
                setShowDownloadButtons(false);

                if (type === 'hr') {
                    GenerateHRXLSX(data.response?.header, data.response?.dtr, data.response?.options);
                } else {
                    GenerateInternalXLSX(data.response?.header, data.response?.dtr, data.response?.options);
                }
            } else {
                setShowDownloadSpinner('');
                toast.fire({
                    icon: 'error',
                    title: 'Download failed: Unexpected response.'
                });
            }
        } catch (error) {
            setShowDownloadSpinner('');

            const errorMessage = error.response?.data?.response || 'Something went wrong while downloading DTR!';
            
            toast.fire({
                icon: 'error',
                title: errorMessage
            });
    
            if (error.response?.data?.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    };

    return (
        <div className={`employee ${isLoadingEmployees ? 'loading' : ''}`}>
            {
                isLoadingEmployees ? (
                    [...Array(5)].map((_, i) => (
                        <div key={i}>
                            <a>
                                <div className="title">
                                    <i className="fa fa-file-excel"></i>
                                    <span title="Loading...">Loading...</span>
                                    <button type="button" title="DOWNLOAD" disabled>
                                        <i className="fa fa-download"></i>
                                    </button>
                                </div>
                                <div className="image">
                                    <img src={dtrimage} alt="Loading..." />
                                </div>
                            </a>
                        </div>
                    ))
                ) : (
                    employees?.length > 0
                        ? (
                            employees?.map((item) => (
                                <div key={item.id}>
                                    <NavLink 
                                        to={`${((role === 'admin' || role === 'management') || accessibleDepartments?.length > 0) 
                                            ? `/dtr/timesheets/${breadcrumbChildSlug}/${breadcrumbGrandchildSlug}/${timesheetPayperiodId}/${item.uname}` 
                                            : `/dtr/timesheets/${breadcrumbChildSlug}/${timesheetPayperiodId}/${item.uname}`}?employeeid=${item.employeeid}&department=${item.department}&departmentcode=${item.departmentcode}&outlet=${item.outlet}&payperiod=${timesheetPayperiod}&payroll=${item.payrolldate}`}
                                        title={item.name}
                                        onClick={() => { handleSetTimesheetEmployeeData(item.uname, item.name) }}
                                    >
                                        {
                                            (role === 'manager' || role === 'supervisor' || role === 'admin' || role === 'management' || accessibleDepartments?.length > 0) && (
                                                <div className="status">
                                                    {
                                                        (item.status === 0 || item.status === 1) ? (
                                                            <h3 className="submitted">Pending</h3>
                                                        ) : item.status === 2 ? (
                                                            <h3 className="approved">Approved</h3>
                                                        ) : (
                                                            <h3 className="disapproved">Disapproved</h3>
                                                        )
                                                    }
                                                </div>
                                            )
                                        }
                                        <div className="title">
                                            <i className="fa fa-file-excel"></i>
                                            <span title={item.name}>{item.name}</span>
                                            {(role === 'admin' || role === 'management') && (
                                                item.status === 2 && (
                                                    <div className={`download ${showDownloadButtons === item.id ? 'active' : ''}`}>
                                                        <button type="button" title="DOWNLOAD" onClick={(e) => { e.preventDefault(); setShowDownloadButtons(prev => prev === item.id ? 0 : item.id); }}>
                                                            <i className="fa fa-ellipsis-v"></i>
                                                        </button>

                                                        <div className={`buttons ${showDownloadButtons === item.id ? 'show' : 'hide'}`}>
                                                            <button type="button" title="Download Internal File" onClick={(e) => { handleDownloadDTR(e, item.uname, 'internal') }}>
                                                                <span>Internal</span> 
                                                                <i className={`fa ${showDownloadSpinner === 'internal' && showDownloadButtons === item.id ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                                                            </button>
                                                            <button type="button" title="Download HR File" onClick={(e) => { handleDownloadDTR(e, item.uname, 'hr') }}>
                                                                <span>HR</span> 
                                                                <i className={`fa ${showDownloadSpinner === 'hr' && showDownloadButtons === item.id ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                        <div className="image">
                                            <img src={dtrimage} alt={item.name} />
                                        </div>
                                    </NavLink>
                                </div>
                            ))
                        ) : (
                            <div className="no-results">
                                <h3>No results found.</h3>
                            </div>
                        )
                )
            }
        </div>
    );
}

export default Employee;